import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import PbrStageBadge from '../../../components/cultivar/pbr/PbrStageBadge';
import DeadlineChip from '../../../components/cultivar/pbr/DeadlineChip';
import type { PbrDashboardSummary, PbrStage } from '../../../types/cultivar';
import { PBR_STAGE_LABELS } from '../../../types/cultivar';
import { FileCheck, UserCheck, ListChecks, Plus, ShieldAlert, Clock } from 'lucide-react';

export default function PbrDashboard() {
  const navigate = useNavigate();
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');
  const [data, setData] = useState<PbrDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }
    pbrService.getDashboard()
      .then(r => {
        if (r.success && r.data) setData(r.data);
        else setError('Failed to load dashboard');
      })
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [hasAccess]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Cultivar module license is required to access PBR workflow.</p>
        </div>
      </div>
    );
  }

  const stageOrder: PbrStage[] = [
    'candidate', 'part1_prep', 'part1_submitted', 'accepted',
    'trial_in_progress', 'part2_prep', 'part2_submitted', 'granted',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="PBR Workflow"
        subtitle="Plant Breeder's Rights applications across all jurisdictions"
        icon={<FileCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/cultivar/pbr/candidates" className="bg-white border border-neutral-200 rounded-xl p-6 hover:shadow-sm transition">
            <div className="flex items-center gap-3"><ListChecks className="h-6 w-6 text-primary-600" /><span className="font-medium">Candidates</span></div>
            <p className="text-sm text-neutral-500 mt-2">Cultivars marked as PBR candidates</p>
          </Link>
          <Link to="/cultivar/pbr/applications" className="bg-white border border-neutral-200 rounded-xl p-6 hover:shadow-sm transition">
            <div className="flex items-center gap-3"><FileCheck className="h-6 w-6 text-primary-600" /><span className="font-medium">Applications</span></div>
            <p className="text-sm text-neutral-500 mt-2">All in-progress and granted applications</p>
          </Link>
          <Link to="/cultivar/pbr/qualified-persons" className="bg-white border border-neutral-200 rounded-xl p-6 hover:shadow-sm transition">
            <div className="flex items-center gap-3"><UserCheck className="h-6 w-6 text-primary-600" /><span className="font-medium">Qualified Persons</span></div>
            <p className="text-sm text-neutral-500 mt-2">QP register for your organisation</p>
          </Link>
        </div>

        {loading && <div className="text-neutral-500">Loading dashboard…</div>}
        {error && <div className="text-red-600">{error}</div>}

        {data && (
          <>
            {/* Stage counts */}
            <section className="bg-white border border-neutral-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Applications by stage</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {stageOrder.map(s => (
                  <div key={s} className="bg-neutral-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-semibold text-neutral-800">{data.byStage[s] || 0}</div>
                    <div className="text-xs text-neutral-500 mt-1">{PBR_STAGE_LABELS[s]}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Upcoming deadlines */}
            <section className="bg-white border border-neutral-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Clock className="h-5 w-5" /> Upcoming Part 1 deadlines</h2>
              {data.upcomingDeadlines.length === 0 ? (
                <p className="text-sm text-neutral-500">No deadlines — no candidates or Part 1-prep applications with sale dates.</p>
              ) : (
                <div className="divide-y divide-neutral-100">
                  {data.upcomingDeadlines.map(d => (
                    <button
                      key={d.id}
                      onClick={() => navigate(`/cultivar/pbr/applications/${d.id}`)}
                      className="w-full flex items-center justify-between py-3 text-left hover:bg-neutral-50 -mx-2 px-2 rounded"
                    >
                      <div>
                        <div className="font-medium">{d.cultivar_trade_name || d.cultivar_name}</div>
                        <div className="text-xs text-neutral-500">{d.jurisdiction} · {d.application_type}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PbrStageBadge stage={d.stage} />
                        <DeadlineChip deadline={d.part1_due_date} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Candidates without application */}
            {data.candidatesWithoutApplication.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Candidates without an application</h2>
                  <Button size="sm" onClick={() => navigate('/cultivar/pbr/applications/new')}>
                    <Plus className="h-4 w-4 mr-1" /> Start application
                  </Button>
                </div>
                <div className="divide-y divide-neutral-100">
                  {data.candidatesWithoutApplication.map(c => (
                    <div key={c.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{c.trade_name || c.name}</div>
                        <div className="text-xs text-neutral-500">
                          AU sale: {c.first_sale_date_au || '—'} · Overseas sale: {c.first_sale_date_overseas || '—'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DeadlineChip deadline={c.part1_deadline} />
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/cultivar/pbr/applications/new?cultivar_id=${c.id}`)}>
                          Start
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Growing trials needed */}
            {data.trialsNeeded.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Growing trials required</h2>
                <div className="divide-y divide-neutral-100">
                  {data.trialsNeeded.map(t => (
                    <button
                      key={t.id}
                      onClick={() => navigate(`/cultivar/pbr/applications/${t.id}`)}
                      className="w-full flex items-center justify-between py-3 text-left hover:bg-neutral-50 -mx-2 px-2 rounded"
                    >
                      <div className="font-medium">{t.cultivar_name}</div>
                      <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <span>{t.jurisdiction}</span>
                        <PbrStageBadge stage={t.stage} />
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
